import { useState } from 'react';
import type { FitnessDecision } from '@dru/types';
import { FITNESS_LABEL } from '@dru/types';
import { api } from '../lib/api';
import { errorMessage } from '../lib/auth';
import { useOfflineQuery, savedAgo } from '../lib/useOfflineQuery';
import { FITNESS_TONE, FitnessPill } from '../components/FitnessMark';
import { longDate } from '../lib/labels';

/**
 * Fitness status, and the certificate behind it.
 *
 * "Fitness" rather than "Certificate" as the section name is deliberate: the
 * certificate is the artifact, the fitness status is what the worker actually
 * holds — and it is the word the domain uses. It also gives restrictions
 * somewhere to live that is not buried inside a PDF.
 */

interface Restriction {
  code: string;
  label: string;
  description: string | null;
  /** The physician's note on this specific restriction, where they left one. */
  note: string | null;
}

interface Cert {
  id: string;
  certificateNumber: string;
  decision: FitnessDecision;
  validFrom: string;
  validUntil: string;
  status: string;
  issuedAt: string;
  /** Decided by the server. Only an ACTIVE certificate is ever current. */
  current: boolean;
  retestDueDate: string | null;
  restrictions: Restriction[];
  /** Findings the physician marked as the worker's to read. */
  notes: Array<{ category: string; finding: string }>;
}

/** Why a certificate is no longer the worker's standing status. */
const NOT_CURRENT: Record<string, string> = {
  REVOKED: 'This certificate has been withdrawn and is no longer valid.',
  SUPERSEDED: 'This certificate has been replaced by a newer one.',
  EXPIRED: 'This certificate has expired.',
};

/**
 * Plain-language meaning. A worker reading "FIT_WITH_RESTRICTION" learns
 * nothing; reading what it means for their shift tomorrow is the point.
 */
const MEANING: Record<FitnessDecision, string> = {
  FURTHER_EVALUATION: 'Further medical evaluation is required before a fitness decision can clear you for work. Contact your occupational health team.',
  FIT: 'You are cleared for your normal work.',
  FIT_WITH_RESTRICTION:
    'You are cleared to work, with specific limits your supervisor must follow.',
  TEMPORARILY_UNFIT:
    'You are not cleared right now. This is expected to change — you will be re-tested on the date below.',
  UNFIT: 'You are not cleared for this work. Speak to your occupational health team.',
};

export function Fitness() {
  // The one screen offline exists for: a worker at a gate with no signal has
  // to be able to show that they are cleared to work.
  const certs = useOfflineQuery<Cert[]>('certificates', () => api.get('/me/certificates'));
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  /**
   * Downloads go through a grant, not a direct link.
   *
   * The document endpoint needs an Authorization header, which an <a href>
   * cannot send, and the grant it returns lives five minutes — a medical
   * document never gets a durable URL. So: ask for the grant, follow it, hand
   * the bytes to the browser.
   */
  async function download(certificateId: string) {
    setDownloading(true);
    setDownloadError(null);
    try {
      const grant = await api.get<{ url: string; certificateNumber: string }>(
        `/me/certificates/${certificateId}/download`);
      const res = await fetch(grant.url, { credentials: 'include' });
      if (!res.ok) throw new Error('The document could not be fetched.');
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `${grant.certificateNumber.replace(/\//g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      setDownloadError(errorMessage(e) || 'The certificate could not be downloaded.');
    } finally {
      setDownloading(false);
    }
  }
  // The server decides. Two client-side heuristics disagreed about this —
  // Home took the first row, this screen preferred ACTIVE and fell back to the
  // first — so a worker whose newest certificate was revoked saw two different
  // certificate numbers in one session.
  const current = certs.data?.find((c) => c.current);
  // Shown separately, and never as a status: a revoked certificate is history.
  const latest = certs.data?.[0];

  const heading = <h1 className="text-base font-semibold text-neutral-900">Your fitness</h1>;

  if (certs.loading) {
    return (
      <div className="space-y-4">
        {heading}
        <div role="status" aria-label="Loading" className="h-32 animate-pulse rounded-xl bg-neutral-100" />
      </div>
    );
  }

  if (!current) {
    // Two different situations, and telling them apart matters: one worker has
    // never been examined, the other held a certificate that has been
    // withdrawn. Showing the second one their old status as though it stood is
    // the failure this screen must not have.
    if (latest) {
      return (
        <div className="space-y-4">
          {heading}
          <div className="rounded-xl bg-neutral-100 px-5 py-6 text-center ring-1 ring-inset ring-neutral-300">
            <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Your current status
            </p>
            <p className="mt-1 text-lg font-semibold text-neutral-900">Not currently certified</p>
            <p className="mt-2 text-sm text-neutral-700">
              {NOT_CURRENT[latest.status] ?? 'Your last certificate is no longer valid.'}{' '}
              Speak to your occupational health team about your next checkup.
            </p>
          </div>

          <div className="rounded-xl border border-neutral-200 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Your last certificate
            </p>
            <p className="mt-1 font-mono text-sm text-neutral-700">{latest.certificateNumber}</p>
            <p className="mt-1 text-xs text-neutral-500">
              Issued {longDate(latest.issuedAt)}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {heading}
        <div className="rounded-xl border border-dashed border-neutral-300 px-5 py-10 text-center">
          <p className="text-sm font-medium text-neutral-800">No certificate yet</p>
          <p className="mt-1 text-sm text-neutral-600">
            Once your checkup is reviewed by the doctor, your fitness certificate will appear here.
          </p>
        </div>
      </div>
    );
  }

  const tone = FITNESS_TONE[current.decision];
  const StatusIcon = tone.icon;
  const expired = new Date(current.validUntil) < new Date();

  return (
    <div className="space-y-4">
      {heading}
      {/* Said before the status, not after it. A certificate can be revoked or
          superseded between the copy being saved and being shown, and someone
          holding this phone up at a gate is entitled to know which they are
          looking at. Hiding the difference is what would make it untrustworthy. */}
      {certs.stale && certs.cachedAt && (
        <div role="status" className="rounded-lg bg-neutral-100 px-4 py-3 text-sm text-neutral-700">
          <span className="font-medium">Saved copy</span> from {savedAgo(certs.cachedAt)}.
          Connect to the internet to check it is still valid.
        </div>
      )}

      {expired ? (
        /* An expired certificate is not a standing status. It used to keep
           its green "Fit for duty" card, with the expiry in a red box beneath
           it — so the first thing a gate guard read was the one thing that was
           no longer true. The expiry leads now, and the decision is shown only
           as what the last certificate said. */
        <div className="rounded-xl bg-overdue-100 px-5 py-6 text-center ring-1 ring-inset ring-overdue-600/25">
          <p className="text-xs font-medium uppercase tracking-wide text-neutral-700">
            Your current status
          </p>
          <p className="mt-1 text-xl font-bold text-overdue-600">Certificate expired</p>
          <p className="mt-2 text-sm text-neutral-800">
            This certificate expired on {onDate(current.validUntil)}. Your next checkup is due — your
            employer will arrange it.
          </p>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs text-neutral-700">
            Last decision: <FitnessPill decision={current.decision} />
          </p>
        </div>
      ) : (
        <div className={`rounded-xl px-5 py-6 text-center ring-1 ring-inset ${tone.bg} ${tone.ring}`}>
          <p className="text-xs font-medium uppercase tracking-wide text-neutral-700">
            Your current status
          </p>
          {/* Status is text first. Colour reinforces it and never carries it
              alone — this workforce skews male, and colour vision deficiency is
              common enough that a coloured badge alone would fail a real share
              of readers. The icon is the third carrier: four different
              shapes for the four outcomes. */}
          <p className={`mt-2 flex items-center justify-center gap-2 text-xl font-bold ${tone.text}`}>
            <StatusIcon aria-hidden className="h-7 w-7 shrink-0" strokeWidth={2.25} />
            {FITNESS_LABEL[current.decision]}
          </p>
          <p className="mt-2 text-sm text-neutral-800">{MEANING[current.decision]}</p>
        </div>
      )}

      {/*
        The restrictions themselves.

        The status card above says a worker's supervisor must follow "specific
        limits"; until now that sentence was the whole of it, and the limits
        were never shown. They are coded rather than free text precisely so
        they can be read mechanically — by a gate guard, by an auditor, and
        here by the person they apply to.
      */}
      {current.restrictions.length > 0 && (
        <section className="rounded-xl border border-restricted-600/30 bg-restricted-100 p-4">
          <h2 className="text-sm font-semibold text-neutral-900">Your work restrictions</h2>
          <p className="mt-1 text-xs text-neutral-700">
            These apply while this certificate is valid. You can show this screen to your
            supervisor.
          </p>
          <ul className="mt-3 space-y-3">
            {current.restrictions.map((r) => (
              <li key={r.code} className="border-t border-restricted-600/20 pt-3 first:border-0 first:pt-0">
                <p className="text-sm font-medium text-neutral-900">{r.label}</p>
                {r.description && (
                  <p className="mt-0.5 text-sm text-neutral-700">{r.description}</p>
                )}
                {r.note && <p className="mt-0.5 text-sm text-neutral-700">{r.note}</p>}
                {/* The code is what a supervisor or auditor quotes back. */}
                <p className="mt-1 text-xs text-neutral-600">
                  Restriction code <span className="font-mono">{r.code}</span>
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* The date the status card promises. TEMPORARILY_UNFIT has always read
          "you will be re-tested on the date below" with nothing below it. */}
      {current.retestDueDate && (
        <div className="rounded-xl border border-neutral-200 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
            Re-test due
          </p>
          <p className="mt-1 text-base font-semibold text-neutral-900">
            {longDate(current.retestDueDate)}
          </p>
          <p className="mt-1 text-sm text-neutral-700">
            Your employer will arrange this. You do not need to book it yourself.
          </p>
        </div>
      )}

      {/* Only findings the physician marked as the worker's to read. The flag
          is the authorisation; everything else stays in the clinical record. */}
      {current.notes.length > 0 && (
        <section className="rounded-xl border border-neutral-200 p-4">
          <h2 className="text-sm font-semibold text-neutral-900">From the doctor</h2>
          <ul className="mt-2 space-y-2">
            {current.notes.map((n, i) => (
              <li key={i} className="text-sm text-neutral-700">
                <span className="text-neutral-500">{n.category}: </span>{n.finding}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="rounded-xl border border-neutral-200 p-4">
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-neutral-500">Certificate number</dt>
            <dd className="text-right font-mono text-neutral-900">{current.certificateNumber}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-neutral-500">Valid from</dt>
            <dd className="text-neutral-900">{onDate(current.validFrom)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-neutral-500">Valid until</dt>
            <dd className={expired ? 'font-medium text-overdue-600' : 'text-neutral-900'}>
              {onDate(current.validUntil)}
            </dd>
          </div>
        </dl>
      </div>

      {/*
        The QR pass is deliberately NOT rendered here yet.

        The verification token is returned exactly once, when the certificate
        is generated, and only its hash is stored — so there is no endpoint
        that can hand it back, by design. Showing a QR here would mean either
        weakening that or inventing a second credential. The download below
        gives the worker the real document, which carries the QR.
      */}
      {downloadError && (
        <div role="alert" className="rounded-lg bg-danger-100 px-4 py-3 text-sm text-danger-600">
          {downloadError}
        </div>
      )}

      <button
        onClick={() => download(current.id)}
        disabled={downloading}
        className="min-h-12 w-full rounded-lg border border-neutral-300 bg-white py-3 text-center text-sm font-medium
                   text-neutral-800 active:bg-neutral-100 disabled:text-neutral-400"
      >
        {downloading ? 'Preparing…' : 'Download certificate (PDF)'}
      </button>
      <p className="-mt-2 text-center text-xs text-neutral-600">
        The PDF carries the QR code that is checked at the gate.
      </p>
    </div>
  );
}

/**
 * A certificate date as a worker reads it.
 *
 * The API sends an ISO timestamp; printing it raw put
 * "2027-09-05T00:00:00.000Z" on the card somebody holds up at a gate.
 */
function onDate(value: string | null | undefined): string {
  if (!value) return '—';
  // Day first, as on the certificate itself — see lib/labels.
  return longDate(value) || value;
}
